/* Avora: bounded presentation-video work and a batched mirror of the native purchase form. */
(() => {
  'use strict';
  if (window.avoraRuntimeReady) return;
  window.avoraRuntimeReady = true;

  // Presentation videos only. Customer videos and section-managed media keep their own controls.
  if ('IntersectionObserver' in window) {
    const tracked = new Set(), visible = new Set(), pending = new WeakSet();
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const excluded = 'avora-customer-videos,avora-premium-videos,[data-avora-managed-media]';
    const eligible = video => video.isConnected && video.closest('main,product-modal') && !video.closest(excluded);
    const pause = video => { if (!video.paused) video.pause(); };
    const play = video => {
      if (document.hidden || motion.matches || !visible.has(video) || !eligible(video) || !video.paused || pending.has(video)) return;
      pending.add(video);
      try {
        Promise.resolve(video.play()).then(() => {
          if (document.hidden || motion.matches || !visible.has(video) || !eligible(video)) pause(video);
        }).catch(() => {}).finally(() => pending.delete(video));
      } catch (_) { pending.delete(video); }
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio > 0) {
          visible.add(entry.target); play(entry.target);
        } else { visible.delete(entry.target); pause(entry.target); }
      });
    }, {threshold: [0, 0.05]});
    const ready = event => play(event.currentTarget);
    const add = video => {
      if (tracked.has(video) || !eligible(video)) return;
      tracked.add(video);
      video.dataset.avoraLoop = '';
      video.autoplay = false; video.removeAttribute('autoplay');
      video.controls = false; video.removeAttribute('controls');
      video.muted = true; video.defaultMuted = true; video.loop = true; video.playsInline = true;
      video.setAttribute('muted', ''); video.setAttribute('playsinline', '');
      video.setAttribute('disablepictureinpicture', ''); video.tabIndex = -1;
      if (!video.hasAttribute('preload')) video.preload = 'none';
      video.addEventListener('loadeddata', ready);
      observer.observe(video);
      if (document.hidden || motion.matches) pause(video);
    };
    const scan = node => {
      if (node.nodeType !== 1 || node.closest(excluded)) return;
      if (node.matches('video')) add(node);
      else node.querySelectorAll('video').forEach(add);
    };
    const remove = video => {
      pause(video); observer.unobserve(video);
      video.removeEventListener('loadeddata', ready);
      tracked.delete(video); visible.delete(video);
    };
    // Only inspect added subtrees. Never rescan every page video after AOV/cart DOM updates.
    const changes = new MutationObserver(records => {
      let removed = false;
      records.forEach(record => {
        record.addedNodes.forEach(scan);
        if (record.removedNodes.length) removed = true;
      });
      if (removed) tracked.forEach(video => { if (!eligible(video)) remove(video); });
    });
    document.querySelectorAll('main video,product-modal video').forEach(add);
    changes.observe(document.body, {childList: true, subtree: true});
    const resume = () => { if (!document.hidden && !motion.matches) visible.forEach(play); };
    document.addEventListener('click', resume, {passive: true});
    document.addEventListener('touchend', resume, {passive: true});
    document.addEventListener('keydown', resume);
    document.addEventListener('visibilitychange', () => { if (document.hidden) tracked.forEach(pause); else resume(); });
    window.addEventListener('pagehide', () => tracked.forEach(pause));
    window.addEventListener('pageshow', resume);
    motion.addEventListener('change', () => { if (motion.matches) tracked.forEach(pause); else resume(); });
  }

  if (!customElements.get('avora-sticky')) customElements.define('avora-sticky', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.source = document.querySelector('product-info');
      this.button = this.querySelector('[data-sticky-add]');
      this.edit = this.querySelector('[data-edit]');
      this.choiceHead = this.querySelector('.av-sticky-choice-head');
      if (!this.source || !this.button || !this.edit || !this.choiceHead) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.defaultLabel = this.button.textContent.trim();
      this.price = this.querySelector('[data-sticky-price]');
      this.titleNode = this.querySelector('.av-sticky-title');
      this.frame = 0; this.adding = false;
      this.schedule = () => {
        if (this.frame || !this.isConnected || (this.hidden && this.choiceHead.hidden)) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; if (this.isConnected) this.update(); });
      };
      this.edit.addEventListener('click', () => {
        if (!this.choiceHead.hidden) { this.closeChoices(); return; }
        const bundle = this.source.querySelector('.Avada-Bundle-Offer__Volume');
        const native = this.source.querySelector('variant-selects');
        const qty = this.source.querySelector('.product-form__quantity');
        const fullSet = this.source.querySelector('avora-full-set:not([data-mode="addon"])[data-active="true"]');
        const nodes = fullSet ? [fullSet] : bundle && bundle.querySelector('.Avada-Volume__Item') ? [bundle] : [native, qty].filter(Boolean);
        if (!nodes.length) return;
        let bottom = window.innerHeight - this.getBoundingClientRect().top + 8;
        nodes.reverse().forEach(node => {
          node.style.setProperty('--choice-bottom', bottom + 'px');
          node.classList.add('av-sticky-choice-source');
          node.style.setProperty('width', 'min(540px, calc(100vw - 24px))', 'important');
          bottom += node.getBoundingClientRect().height + 4;
        });
        this.choiceHead.hidden = false;
        this.edit.setAttribute('aria-expanded', 'true');
        this.update();
      }, {signal});
      this.querySelector('[data-close]').addEventListener('click', () => { this.closeChoices(); this.edit.focus(); }, {signal});
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && !this.choiceHead.hidden) { this.closeChoices(); this.edit.focus(); }
      }, {signal});
      this.button.addEventListener('click', () => {
        if (this.adding || this.button.disabled) return;
        this.closeChoices(); this.adding = true; this.button.disabled = true;
        this.addTimer = window.setTimeout(() => {
          this.addTimer = 0;
          if (!this.isConnected) return;
          const button = (this.source.querySelector('avora-full-set:not([data-mode="addon"])[data-active="true"] [data-avfs-add]') || this.source.querySelector('product-form [name="add"]'));
          // Keep AOV and Shopify in charge of quantities, variants, discounts and cart updates.
          if (button && !button.disabled && button.getAttribute('aria-disabled') !== 'true') button.click();
          this.adding = false; this.update();
        }, 350);
      }, {signal});
      this.source.addEventListener('change', this.schedule, {signal});
      this.source.addEventListener('input', this.schedule, {signal});
      window.addEventListener('resize', this.schedule, {passive: true, signal});
      this.observer = new MutationObserver(this.schedule);
      this.observer.observe(this.source, {subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'disabled', 'aria-disabled', 'value']});
      this.update();
      const target = this.source.querySelector('product-form [name="add"],avora-full-set[data-mode="product"] [data-avfs-add]') || this.source.querySelector('media-gallery');
      if (target && 'IntersectionObserver' in window) {
        this.intersection = new IntersectionObserver(entries => {
          const hidden = entries[0].boundingClientRect.bottom > 0;
          if (this.hidden !== hidden) this.hidden = hidden;
          if (hidden) this.closeChoices(); else this.schedule();
        }, {threshold: 0});
        this.intersection.observe(target);
      }
    }
    closeChoices() {
      this.source?.querySelectorAll('.av-sticky-choice-source').forEach(node => {
        node.classList.remove('av-sticky-choice-source');
        node.style.removeProperty('width'); node.style.removeProperty('--choice-bottom');
      });
      if (this.choiceHead) this.choiceHead.hidden = true;
      this.edit?.setAttribute('aria-expanded', 'false');
    }
    update() {
      if (!this.choiceHead.hidden) {
        const blocks = Array.from(this.source.querySelectorAll('.av-sticky-choice-source'));
        if (blocks.length) {
          const bottom = this.getBoundingClientRect().bottom - Math.min(...blocks.map(node => node.getBoundingClientRect().top)) + 4 + 'px';
          if (this.choiceHead.style.bottom !== bottom) this.choiceHead.style.bottom = bottom;
        }
      }
      const sourceButton = (this.source.querySelector('avora-full-set:not([data-mode="addon"])[data-active="true"] [data-avfs-add]') || this.source.querySelector('product-form [name="add"]'));
      if (!sourceButton) return;
      const disabled = this.adding || sourceButton.disabled || sourceButton.getAttribute('aria-disabled') === 'true';
      if (this.button.disabled !== disabled) this.button.disabled = disabled;
      const fullSet = this.source.querySelector('avora-full-set:not([data-mode="addon"])[data-active="true"]');
      const label = fullSet ? fullSet.querySelector('[data-avfs-add-label]').textContent : disabled && !this.adding ? sourceButton.textContent.trim() : this.defaultLabel;
      if (this.button.textContent !== label) this.button.textContent = label;
      const tier = fullSet ? null : this.source.querySelector('.Avada-Volume__Item--Selected');
      const price = fullSet?.querySelector('[data-avfs-price]') || tier?.querySelector('.AOV-Offer__DiscountPrice') || sourceButton.querySelector('.avh-button-price') || this.source.querySelector('[data-avora-unit-cost]') || this.source.querySelector('[id^="price-"] .price__sale .price-item--sale') || this.source.querySelector('[id^="price-"] .price-item--regular');
      if (price && this.price && this.price.textContent !== price.textContent.trim()) this.price.textContent = price.textContent.trim();
      const selector = this.source.querySelector('variant-selects');
      const title = fullSet ? 'Full Set · ' + Array.from(fullSet.querySelectorAll('[data-avfs-color]')).map(node => node.value).join(' / ') : tier ? Array.from(tier.querySelectorAll('.Avada-VariantSelection__SelectedText')).map(node => node.textContent).join(' / ') : selector ? Array.from(selector.querySelectorAll('input:checked,select option:checked')).map(node => node.value).join(' / ') : '';
      if (this.titleNode && this.titleNode.textContent !== title) this.titleNode.textContent = title;
    }
    disconnectedCallback() {
      this.observer?.disconnect(); this.intersection?.disconnect();
      this.abort?.abort(); this.abort = null;
      cancelAnimationFrame(this.frame); clearTimeout(this.addTimer);
      this.closeChoices();
    }
  });
})();

